import { AriaButtonProps } from '@react-types/button';
import { PressEvent } from '@react-types/shared';
import classnames from 'classnames';
import React, { useRef } from 'react';
import { useButton } from 'react-aria';

import { Loader } from '../Loader/Loader';
import './Button.scss';

export interface ButtonProps extends AriaButtonProps<'button'> {
  children: any;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'warning';
  full?: boolean;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  autoFocus?: boolean;
  isDisabled?: boolean;
  onClick?: (e: PressEvent) => void;
  onClickStart?: (e: PressEvent) => void;
  onClickEnd?: (e: PressEvent) => void;
  onClickChange?: (isPressed: boolean) => void;
  onClickUp?: (e: PressEvent) => void;
  size?: string;
  theme?: string;
  className?: string;
}

export function Button({
  variant = 'primary',
  full = false,
  isDisabled = false,
  loading = false,
  size = 'normal',
  type = 'button',
  theme = 'light',
  className,
  children,
  ...props
}: ButtonProps): React.ReactElement {
  const ref = useRef<any>();
  const { buttonProps } = useButton(
    {
      isDisabled,
      type,
      ...props, // props contains the PressEvents
    },
    ref
  );

  const classNames = classnames('button', size, variant, theme, {
    [`${full}`]: full,
    [`disabled`]: isDisabled,
    [`${loading}`]: loading,
    [`${className}`]: className,
    [`arc-dark`]: theme === 'dark',
    [`${className}__button`]: !!className,
    [`${className}__button--${size}`]: !!className,
    [`${className}__button--${variant}`]: !!className,
    [`${className}__button--full`]: !!className && full,
    [`${className}__button--disabled`]: !!className && isDisabled,
    [`${className}__button--loading`]: !!className && loading,
  });

  return (
    <button className={classNames} {...buttonProps} ref={ref}>
      {loading && <Loader />}
      {children}
    </button>
  );
}
